<!-- src/components/CreateRecipeModal.vue -->
<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { CREATE_RECIPE } from '../graphql/mutations'
import { GET_ALL_RECIPE_CATEGORIES, GET_ALL_INGREDIENTS } from '../graphql/queries'
import { showError, showSuccess } from '../utils/errorHandling'
import UnsavedChangesDialog from './UnsavedChangesDialog.vue'
import RichTextEditor from './RichTextEditor.vue'
import FormErrorSummary from './FormErrorSummary.vue'
import { vm } from '../utils/validationMessages'
import { useBodyScrollLock } from '../composables/useBodyScrollLock'

// Props
interface Props {
  show: boolean
}

const props = defineProps<Props>()
useBodyScrollLock(() => props.show)

// Emits
const emit = defineEmits<{
  close: []
  created: [recipe: any]
}>()

// Reactive form data
const formData = ref({
  title: '',
  description: '',
  content: '',
  category: '',
  ingredients: [] as Array<{
    id?: string // Optional for new ingredients
    name: string
    quantity: number | null
    unit: string
    isNew?: boolean // Flag to identify new ingredients
  }>
})

// Form validation
const formErrors = ref({
  title: '',
  description: '',
  category: '',
  ingredients: ''
})

// State
const currentStep = ref(1)
const totalSteps = 3
const searchIngredient = ref('')
const showIngredientDropdown = ref(false)
const isCreatingNewCategory = ref(false)
const isCreatingNewIngredient = ref(false)
const showUnsavedChangesDialog = ref(false)
const pendingClose = ref(false)
const step1NextAttempted = ref(false)
const step2NextAttempted = ref(false)

// GraphQL queries
const { result: categoriesResult, loading: loadingCategories } = useQuery(GET_ALL_RECIPE_CATEGORIES)
const { result: ingredientsResult, loading: loadingIngredients, refetch: refetchIngredients } = useQuery(GET_ALL_INGREDIENTS)

// Create recipe mutation
const { mutate: createRecipe, loading: creatingRecipe } = useMutation(CREATE_RECIPE, {
  update: (cache, { data }) => {
    // Update the GET_ALL_INGREDIENTS cache with any new ingredients created during recipe creation
    if (data?.createRecipe?.ingredients) {
      try {
        const existingData = cache.readQuery({ query: GET_ALL_INGREDIENTS })
        if (existingData) {
          // Get all ingredients from the newly created recipe
          const recipeIngredients = data.createRecipe.ingredients
          
          // Find which ingredients are new (not in the existing cache)
          const newIngredients = recipeIngredients.filter((ing: any) => {
            return ing.id && !(existingData as any).getAllIngredients?.some((existing: any) => 
              existing.id === ing.id
            )
          })
          
          // If there are new ingredients, add them to the cache
          if (newIngredients.length > 0) {
            cache.writeQuery({
              query: GET_ALL_INGREDIENTS,
              data: {
                getAllIngredients: [...(existingData as any).getAllIngredients || [], ...newIngredients]
              }
            })
          }
        } else {
          // If the query isn't in cache yet, initialize it with the recipe ingredients
          const recipeIngredients = data.createRecipe.ingredients.filter((ing: any) => ing.id)
          if (recipeIngredients.length > 0) {
            cache.writeQuery({
              query: GET_ALL_INGREDIENTS,
              data: {
                getAllIngredients: recipeIngredients
              }
            })
          }
        }
      } catch (error) {
        console.error('Error updating ingredients cache:', error)
      }
    }
  },
  refetchQueries: [{ query: GET_ALL_INGREDIENTS }]
})

// Computed properties
const categories = computed(() => {
  return categoriesResult.value?.getAllRecipeCategories || []
})

const allIngredients = computed(() => {
  return ingredientsResult.value?.getAllIngredients || []
})

const filteredIngredients = computed(() => {
  if (!searchIngredient.value) return allIngredients.value

  const search = searchIngredient.value.toLowerCase()
  return allIngredients.value.filter((ingredient: any) =>
    ingredient.name.toLowerCase().includes(search)
  )
})

const availableIngredients = computed(() => {
  const usedIngredientNames = formData.value.ingredients.map(ing => ing.name.toLowerCase())
  return filteredIngredients.value.filter((ingredient: any) =>
    !usedIngredientNames.includes(ingredient.name.toLowerCase())
  )
})

const canProceedToStep2 = computed(() => {
  return formData.value.title.trim() !== '' &&
    formData.value.description.trim() !== '' &&
    formData.value.category !== ''
})

const canProceedToStep3 = computed(() => {
  return formData.value.ingredients.length > 0 &&
    formData.value.ingredients.every(ing =>
      ing.quantity !== null && ing.quantity > 0 && ing.unit.trim() !== ''
    )
})

const canSubmit = computed(() => {
  return canProceedToStep2.value && canProceedToStep3.value
})

const hasUnsavedChanges = computed(() => {
  return formData.value.title.trim() !== '' ||
    formData.value.description.trim() !== '' ||
    formData.value.category !== '' ||
    formData.value.ingredients.length > 0
})

// Methods
const resetForm = () => {
  formData.value = {
    title: '',
    description: '',
    content: '',
    category: '',
    ingredients: []
  }
  formErrors.value = {
    title: '',
    description: '',
    category: '',
    ingredients: ''
  }
  currentStep.value = 1
  searchIngredient.value = ''
  showIngredientDropdown.value = false
  isCreatingNewCategory.value = false
  isCreatingNewIngredient.value = false
  step1NextAttempted.value = false
  step2NextAttempted.value = false
}

const attemptClose = () => {
  if (hasUnsavedChanges.value) {
    pendingClose.value = true
    showUnsavedChangesDialog.value = true
  } else {
    closeModal()
  }
}

const closeModal = () => {
  resetForm()
  showUnsavedChangesDialog.value = false
  pendingClose.value = false
  emit('close')
}

const keepEditing = () => {
  showUnsavedChangesDialog.value = false
  pendingClose.value = false
}

const discardChanges = () => {
  closeModal()
}

const validateStep1 = () => {
  formErrors.value.title = !formData.value.title.trim() ? vm.recipeTitle.required : ''
  formErrors.value.description = !formData.value.description.trim() ? vm.recipeDescription.required : ''
  formErrors.value.category = !formData.value.category ? vm.recipeCategory.required : ''

  return !formErrors.value.title && !formErrors.value.description && !formErrors.value.category
}

const validateStep2 = () => {
  formErrors.value.ingredients = formData.value.ingredients.length === 0 ? vm.recipeIngredients.required : ''

  return !formErrors.value.ingredients
}

// Blur handlers for step 1 — give per-field feedback as user fills the form
const blurTitle = () => { if (step1NextAttempted.value || formData.value.title) formErrors.value.title = !formData.value.title.trim() ? vm.recipeTitle.required : '' }
const blurDescription = () => { if (step1NextAttempted.value || formData.value.description) formErrors.value.description = !formData.value.description.trim() ? vm.recipeDescription.required : '' }
const blurCategory = () => { if (step1NextAttempted.value || formData.value.category) formErrors.value.category = !formData.value.category ? vm.recipeCategory.required : '' }

const step1ErrorSummary = computed(() => {
  if (!step1NextAttempted.value) return []
  const items = []
  if (formErrors.value.title) items.push({ field: 'title', label: 'Recipe Title', message: formErrors.value.title })
  if (formErrors.value.description) items.push({ field: 'description', label: 'Description', message: formErrors.value.description })
  if (formErrors.value.category) items.push({ field: 'category', label: 'Category', message: formErrors.value.category })
  return items
})

const nextStep = () => {
  if (currentStep.value === 1) {
    step1NextAttempted.value = true
    if (validateStep1()) currentStep.value = 2
  } else if (currentStep.value === 2) {
    step2NextAttempted.value = true
    if (validateStep2()) currentStep.value = 3
  }
}

const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}

const addIngredient = (ingredient: any) => {
  formData.value.ingredients.push({
    id: ingredient.id,
    name: ingredient.name,
    quantity: null,
    unit: '',
    isNew: false
  })
  searchIngredient.value = ''
  showIngredientDropdown.value = false
}

const addNewIngredient = () => {
  const ingredientName = searchIngredient.value.trim()
  if (!ingredientName) return

  // Check if ingredient already exists in the list
  const exists = formData.value.ingredients.some(ing =>
    ing.name.toLowerCase() === ingredientName.toLowerCase()
  )

  if (!exists) {
    formData.value.ingredients.push({
      name: ingredientName,
      quantity: null,
      unit: '',
      isNew: true
    })
  }

  searchIngredient.value = ''
  showIngredientDropdown.value = false
  isCreatingNewIngredient.value = false
}

const removeIngredient = (index: number) => {
  formData.value.ingredients.splice(index, 1)
}

const submitRecipe = async () => {
  if (!canSubmit.value) return

  try {
    // Prepare ingredients for the mutation
    const ingredientObjects = formData.value.ingredients.map(ingredient => ({
      name: ingredient.name,
      quantity: ingredient.quantity!,
      unit: ingredient.unit
    }))

    const result = await createRecipe({
      data: {
        title: formData.value.title,
        description: formData.value.description,
        content: formData.value.content || null,
        category: formData.value.category,
        ingredients: ingredientObjects
      }
    })

    showSuccess('Recipe created successfully!')
    emit('created', result?.data?.createRecipe)
    closeModal()
  } catch (error) {
    showError(error, 'create recipe')
  }
}

// Watch for modal visibility changes
watch(() => props.show, async (show) => {
  if (show) {
    // Refetch ingredients when modal opens to get the latest data
    await refetchIngredients()
  } else {
    resetForm()
  }
})

// Handle ingredient search focus
const handleIngredientSearchFocus = () => {
  showIngredientDropdown.value = true
}

const handleIngredientSearchBlur = () => {
  // Delay hiding to allow clicking on dropdown items
  setTimeout(() => {
    showIngredientDropdown.value = false
  }, 200)
}

// Handle category selection mode
const toggleCategoryMode = () => {
  isCreatingNewCategory.value = !isCreatingNewCategory.value
  if (isCreatingNewCategory.value) {
    formData.value.category = ''
  }
}

// Handle ingredient selection mode
const toggleIngredientMode = () => {
  isCreatingNewIngredient.value = !isCreatingNewIngredient.value
  searchIngredient.value = ''
  showIngredientDropdown.value = false
}

// Add ingredient from search when pressing Enter
const addIngredientFromSearch = () => {
  if (availableIngredients.value.length > 0) {
    addIngredient(availableIngredients.value[0])
  } else if (searchIngredient.value.trim()) {
    addNewIngredient()
  }
}

// Add multiple ingredients (comma-separated)
const addMultipleIngredients = () => {
  const ingredients = searchIngredient.value
    .split(',')
    .map(name => name.trim())
    .filter(name => name.length > 0)

  ingredients.forEach(ingredientName => {
    // Check if ingredient already exists in the list
    const exists = formData.value.ingredients.some(ing =>
      ing.name.toLowerCase() === ingredientName.toLowerCase()
    )

    if (!exists) {
      formData.value.ingredients.push({
        name: ingredientName,
        quantity: null,
        unit: '',
        isNew: true
      })
    }
  })

  searchIngredient.value = ''
}
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="attemptClose">
    <!-- Modal -->
    <div class="modal-content modal-content-extra-large max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true"
      aria-labelledby="create-recipe-title">
      <!-- Header -->
      <div class="modal-header">
        <div>
          <h2 id="create-recipe-title" class="text-2xl font-bold text-gray-900">Create New Recipe</h2>
          <p class="text-gray-600 mt-1">Step {{ currentStep }} of {{ totalSteps }}</p>
        </div>
        <button @click="attemptClose" class="modal-close-button" aria-label="Close modal">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <!-- Progress Bar -->
        <div class="mb-8">
          <div class="flex items-center">
            <div v-for="step in totalSteps" :key="step" class="flex items-center">
              <div :class="[
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                step <= currentStep
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-600'
              ]">
                {{ step }}
              </div>
              <div v-if="step < totalSteps" :class="[
                'w-16 h-1 mx-2',
                step < currentStep ? 'bg-green-500' : 'bg-gray-200'
              ]"></div>
            </div>
          </div>
          <div class="flex justify-between mt-2 text-sm">
            <span :class="currentStep >= 1 ? 'text-green-600 font-medium' : 'text-gray-600'">Basic Info</span>
            <span :class="currentStep >= 2 ? 'text-green-600 font-medium' : 'text-gray-600'">Ingredients</span>
            <span :class="currentStep >= 3 ? 'text-green-600 font-medium' : 'text-gray-600'">Review</span>
          </div>
        </div>

        <!-- Step 1: Basic Information -->
        <div v-if="currentStep === 1" class="space-y-6">
          <FormErrorSummary :errors="step1ErrorSummary" />

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Recipe Title <span class="text-red-500 ml-0.5" aria-label="required">*</span>
            </label>
            <input v-model="formData.title" type="text" placeholder="Enter recipe title..."
              class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-[16px] sm:text-sm"
              :class="formErrors.title ? 'border-red-500 bg-red-50' : 'border-gray-300'"
              @blur="blurTitle" />
            <p v-if="formErrors.title" class="text-red-500 text-sm mt-1" role="alert">{{ formErrors.title }}</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Description <span class="text-red-500 ml-0.5" aria-label="required">*</span>
            </label>
            <textarea v-model="formData.description" rows="4" placeholder="Describe your recipe..."
              class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-[16px] sm:text-sm"
              :class="formErrors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'"
              @blur="blurDescription"></textarea>
            <p v-if="formErrors.description" class="text-red-500 text-sm mt-1" role="alert">{{ formErrors.description }}</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Instructions <span class="text-gray-400 font-normal">(optional)</span></label>
            <RichTextEditor v-model="formData.content" />
          </div>

          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">
                Category <span class="text-red-500 ml-0.5" aria-label="required">*</span>
              </label>
              <button @click="toggleCategoryMode" type="button"
                class="text-sm text-green-600 hover:text-green-700 font-medium transition-colors">
                {{ isCreatingNewCategory ? 'Select existing' : 'Create new' }}
              </button>
            </div>

            <!-- Existing Category Selection -->
            <select v-if="!isCreatingNewCategory" v-model="formData.category"
              class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-[16px] sm:text-sm"
              :class="formErrors.category ? 'border-red-500 bg-red-50' : 'border-gray-300'"
              :disabled="loadingCategories"
              @blur="blurCategory">
              <option value="">Select a category</option>
              <option v-for="category in categories" :key="category.id" :value="category.name">
                {{ category.name }}
              </option>
            </select>

            <!-- New Category Input -->
            <input v-else v-model="formData.category" type="text" placeholder="Enter new category name..."
              class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-[16px] sm:text-sm"
              :class="formErrors.category ? 'border-red-500 bg-red-50' : 'border-gray-300'"
              @blur="blurCategory" />

            <p v-if="formErrors.category" class="text-red-500 text-sm mt-1" role="alert">{{ formErrors.category }}</p>
            <p v-if="loadingCategories && !isCreatingNewCategory" class="text-gray-500 text-sm mt-1">Loading categories...</p>
            <p v-if="isCreatingNewCategory" class="text-gray-500 text-sm mt-1">This will create a new category if it doesn't exist</p>
          </div>
        </div>

        <!-- Step 2: Ingredients -->
        <div v-if="currentStep === 2" class="space-y-6">
          <!-- Add Ingredient Search -->
          <div class="relative">
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">Add Ingredients *</label>
              <button @click="toggleIngredientMode" type="button"
                class="text-sm text-green-600 hover:text-green-700 font-medium transition-colors">
                {{ isCreatingNewIngredient ? 'Search existing' : 'Create new' }}
              </button>
            </div>

            <!-- Search existing ingredients -->
            <div v-if="!isCreatingNewIngredient">
              <input v-model="searchIngredient" type="text" placeholder="Search ingredients..."
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                @focus="handleIngredientSearchFocus" @blur="handleIngredientSearchBlur"
                @keydown.enter.prevent="addIngredientFromSearch" />

              <!-- Ingredient Dropdown -->
              <div v-if="showIngredientDropdown && availableIngredients.length > 0"
                class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                <button v-for="ingredient in availableIngredients.slice(0, 10)" :key="ingredient.id"
                  @click="addIngredient(ingredient)"
                  class="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none">
                  {{ ingredient.name }}
                </button>
              </div>

              <!-- Show create option if no matches -->
              <div v-if="showIngredientDropdown && availableIngredients.length === 0 && searchIngredient.trim()"
                class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                <button @click="addNewIngredient"
                  class="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none text-green-600">
                  Create "{{ searchIngredient.trim() }}"
                </button>
              </div>
            </div>

            <!-- Create new ingredient -->
            <div v-else class="space-y-3">
              <input v-model="searchIngredient" type="text"
                placeholder="Enter ingredient name (or comma-separated list)..."
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                @keydown.enter.prevent="addMultipleIngredients" />
              <div class="flex gap-2">
                <button @click="addMultipleIngredients" :disabled="!searchIngredient.trim()" class="btn-primary">
                  Add Ingredient(s)
                </button>
              </div>
              <p class="text-gray-500 text-xs">Tip: You can enter multiple ingredients separated by commas</p>
            </div>

            <p v-if="loadingIngredients && !isCreatingNewIngredient" class="text-gray-500 text-sm mt-1">Loading
              ingredients...</p>
          </div>

          <!-- Selected Ingredients -->
          <div v-if="formData.ingredients.length > 0">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Selected Ingredients ({{ formData.ingredients.length }})
            </h3>
            <div class="space-y-4">
              <div v-for="(ingredient, index) in formData.ingredients" :key="ingredient.id"
                class="bg-gray-50 rounded-lg p-4 space-y-3">
                <!-- Ingredient Name -->
                <div class="flex items-center justify-between">
                  <p class="font-medium text-gray-900 text-lg">{{ ingredient.name }}</p>
                  <button @click="removeIngredient(index)"
                    class="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title="Remove ingredient">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                <!-- Quantity and Unit Inputs -->
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                    <input v-model="ingredient.quantity" type="number" step="0.1" min="0" placeholder="e.g., 2"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all" />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                    <input v-model="ingredient.unit" type="text" placeholder="e.g., cups, tsp"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base transition-all" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-8 text-gray-500">
            <svg class="mx-auto mb-3 h-10 w-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p>No ingredients added yet</p>
            <p class="text-sm">Search and add ingredients above</p>
          </div>

          <p v-if="formErrors.ingredients" class="text-red-500 text-sm" role="alert">{{ formErrors.ingredients }}</p>
        </div>

        <!-- Step 3: Review -->
        <div v-if="currentStep === 3" class="space-y-6">
          <div class="bg-gray-50 rounded-lg p-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Recipe Summary</h3>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 class="font-medium text-gray-900 mb-2">Basic Information</h4>
                <div class="space-y-2 text-sm">
                  <div><span class="text-gray-600">Title:</span> {{ formData.title }}</div>
                  <div><span class="text-gray-600">Category:</span> {{ formData.category || 'Unknown' }}</div>
                  <div><span class="text-gray-600">Description:</span> {{ formData.description }}</div>
                </div>
              </div>

              <div>
                <h4 class="font-medium text-gray-900 mb-2">Ingredients ({{ formData.ingredients.length }})</h4>
                <div class="space-y-1 text-sm max-h-40 overflow-y-auto">
                  <div v-for="ingredient in formData.ingredients" :key="ingredient.id" class="flex justify-between">
                    <span>{{ ingredient.name }}</span>
                    <span class="text-gray-600">{{ ingredient.quantity }} {{ ingredient.unit }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div class="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
          <button @click="prevStep" v-if="currentStep > 1" class="btn-secondary">
            Previous
          </button>
          <div v-else></div>

          <div class="flex gap-3">
            <button @click="attemptClose" class="btn-secondary">
              Cancel
            </button>

            <button v-if="currentStep < totalSteps" @click="nextStep"
              class="btn-primary"
              :class="(currentStep === 1 && !canProceedToStep2) || (currentStep === 2 && !canProceedToStep3) ? 'opacity-60' : 'hover:opacity-90'">
              Next
            </button>

            <button v-else @click="submitRecipe" :disabled="creatingRecipe || !canSubmit"
              class="btn-primary flex items-center gap-2">
              <div v-if="creatingRecipe" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {{ creatingRecipe ? 'Creating...' : 'Create Recipe' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Unsaved Changes Dialog -->
  <UnsavedChangesDialog 
    :show="showUnsavedChangesDialog"
    @keep-editing="keepEditing"
    @discard="discardChanges"
  />
</template>

<style scoped>
/* Custom scrollbar for ingredient list */
.max-h-40::-webkit-scrollbar {
  width: 4px;
}

.max-h-40::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.max-h-40::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.max-h-40::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>
